/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import BaseEditor, { ActionComposerEditorArgs } from 'cio/components/action-composer/base-glimmer/editor';

import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import LogModel from 'cio/models/log';
import { CreateEventPreview } from 'cio/utils/previews';
import AnalyticsService from 'cio/services/analytics';
import RouterService from '@ember/routing/router-service';
import { DataActionConditionType, DataActionTriggerAttributeDropdownItem } from 'cio/utils/campaigns/data-action';
import CustomerModel from 'cio/models/customer';
import BroadcastTriggerModel from 'cio/models/broadcast-trigger';
import DataActionBodyAttributeRows from 'cio/app-models/data-action/body-attribute-rows';
import EnvironmentService from 'cio/services/environment';
import { tracked } from '@glimmer/tracking';
import DS from 'ember-data';
import { task, timeout } from 'ember-concurrency';
import getPeopleFilters from 'cio/utils/get-people-filters';
import serializeFilters from 'cio/utils/conditions/serialize/filter';
import { isEmpty } from '@ember/utils';
import { queryEncode } from 'cio/helpers/query';
import CreateEventActionEventName from 'cio/app-models/create-event/event-name';
import { BatchUpdateTemplateType } from 'cio/utils/actions/batch-update';
import { SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';
import { findOperator } from 'cio/utils/conditions-operators';
import AccountService from 'cio/services/account';
import Ember from 'ember';
import lodashGet from 'lodash.get';
import config from 'cio/config/environment';
import { JsonAttributeOperator } from 'cio/utils/conditions/json-attributes';
import { equalityOperators, existenceOperators } from 'cio/utils/operators';
import CampaignModel from 'cio/models/campaign';

interface BatchUpdateEditorArgs extends ActionComposerEditorArgs {
  templateType: BatchUpdateTemplateType | null;
  supportedConditionTypes: DataActionConditionType[];
  selectedCustomer?: CustomerModel;
  selectedEvent?: LogModel;
  selectedTrigger?: BroadcastTriggerModel;
  preview: CreateEventPreview;
  conditions: SerializedLegacyFilter[];
  updateConditions: (conditions: SerializedLegacyFilter[]) => unknown;
  selectTemplateType: (templateType: BatchUpdateTemplateType) => unknown;
  eventName: CreateEventActionEventName;
  body: DataActionBodyAttributeRows;
  addBodyRow: () => unknown;
  removeBodyRow: (index: number) => unknown;
  updateHighlight: (highlight: DataActionTriggerAttributeDropdownItem | null) => unknown;
  previewDidChange?: () => unknown;
  isLoadingInitialMergeData?: boolean;
  campaign: CampaignModel;
  hasReadOnlyPermission: boolean;
}

interface BatchUpdateEditorSignature {
  Args: BatchUpdateEditorArgs;
  Element: HTMLDivElement;
}

export default class BatchUpdateEditor extends BaseEditor<BatchUpdateEditorSignature> {
  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service router!: RouterService;
  @service store!: DS.Store;

  DataActionConditionType = DataActionConditionType;
  BatchUpdateTemplateType = BatchUpdateTemplateType;

  allowedConditionListOperators = [
    ...equalityOperators.map((o) => o.value),
    ...existenceOperators.map((o) => o.value),
    ...Object.values(JsonAttributeOperator),
  ];

  constructor(owner: unknown, args: BatchUpdateEditorSignature['Args']) {
    super(owner, args);

    this.fetchRecipients.perform();
  }

  @tracked isShowingFeedbackModal = false;
  @tracked isShowingPeopleLimitFeedbackModal = false;

  @action
  trackPreviewErrors(_: HTMLElement, [errors]: [{ field: string; label: string; errors: string[] }[]]) {
    if (this.args.isPreviewLoading) {
      return;
    }

    let messages = errors.map((e) => `${e.label}: ${e.errors.join(', ')}`);

    this.analytics.track('user', 'error_encountered', {
      currentUrl: this.router.currentURL,
      message: messages,
      source: 'Batch Update',
    });
  }

  recipientLimitBannerTitle = 'Your conditions match too many people';

  @action
  trackRecipientLimitBanner() {
    this.analytics.track('user', 'error_encountered', {
      currentUrl: this.router.currentURL,
      message: this.recipientLimitBannerTitle,
      source: 'Batch Update',
    });
  }

  UPDATE_TYPES = [
    {
      id: BatchUpdateTemplateType.AttributeUpdate,
      icon: 'person',
      label: 'Update profile attributes',
      description: 'Edit, add or delete profile attributes for existing people.',
    },
    {
      id: BatchUpdateTemplateType.CreateEvent,
      icon: 'event',
      label: 'Send an event',
      description: 'Create an event and associate it with existing people.',
    },
  ];

  @action
  updateConditions(conditions: any) {
    this.args.updateConditions(conditions);
  }

  @tracked recipientCount = 0;

  private transformComparisonConditionsToPeopleFilters(conditions: SerializedLegacyFilter[]): SerializedLegacyFilter[] {
    let transformedConditions = conditions.map((condition) => {
      // Needed for TypeScript to not complain about types, since one of the
      // SerializedLegacyFilter sub types doesn't contain the operator field
      // (but those conditions aren't used here)
      if (!('operator' in condition)) {
        return condition;
      }

      if (condition.operator == null) {
        return condition;
      }

      let operatorDefinition = findOperator(condition.operator);

      if (condition.type !== 'attribute_compare' || !operatorDefinition?.hasPredicate) {
        return condition;
      }

      if (condition.value_source === 'customer_attribute' && condition.value != null) {
        let value = lodashGet(this.args.selectedCustomer?.attrs ?? {}, condition.value) ?? '';

        return {
          field: condition.field,
          operator: condition.operator,
          type: 'attribute',
          value: value as string,
        };
      }

      if (condition.value_source === 'event_attribute' && condition.value != null) {
        let value = lodashGet(this.args.selectedEvent?.attrs ?? {}, condition.value) ?? '';

        return {
          field: condition.field,
          operator: condition.operator,
          type: 'attribute',
          value: value as string,
        };
      }

      return condition;
    });

    return transformedConditions;
  }

  fetchRecipients = task({ restartable: true }, async () => {
    if (!this.hasConditions) {
      this.recipientCount = 0;
      return;
    }

    // Prevent spamming the API with too many requests while the sidebar
    // suggestions are still loading.
    // We use sample data from the sidebar to populate the people filters, so we
    // need to wait for that data to be loaded
    if (!Ember.testing && this.args.isLoadingInitialMergeData === true) {
      while (this.args.isLoadingInitialMergeData) {
        await timeout(config.APP.shortDelay);
      }
    }

    let filters = this.transformComparisonConditionsToPeopleFilters(this.args.conditions);
    let transformedFilters = getPeopleFilters(filters);
    let serializedFilters =
      transformedFilters == null || isEmpty(transformedFilters) ? transformedFilters : queryEncode(transformedFilters);

    let result = await this.store.query('customer', { filters: serializedFilters });

    this.recipientCount = (result as any).meta.pagination.total;
  });

  get hasConditions() {
    return this.args.conditions != null && this.args.conditions.length > 0;
  }

  get peopleFilters() {
    if (!this.hasConditions) {
      return undefined;
    }
    let filters = this.transformComparisonConditionsToPeopleFilters(this.args.conditions);

    return queryEncode(serializeFilters(filters, 'and', true));
  }

  get hasValidTemplateType() {
    return (
      this.args.templateType != null &&
      !isEmpty(this.args.templateType) &&
      (Object.values(BatchUpdateTemplateType) as string[]).includes(this.args.templateType)
    );
  }

  get shouldShowConditionSection() {
    return this.hasValidTemplateType;
  }

  get shouldShowEditorSection() {
    return this.hasValidTemplateType && this.hasConditions;
  }
}
